import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { SftpImportConfigurationWorkflow, CalculationModeType, InventoryIdentificationMatchMode, SupplierIdentificationMatchMode, getCalculationModeTypeDescriptions, getInventoryIdentificationMatchModeDescriptions, getSupplierIdentificationMatchModeDescriptions } from '../../../store/Models/Api/SftpImportModels';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { TagsInput } from '../components/TagsInput';
import CheckBox from '../../core/CheckBox';
import { DropdownMenu } from '../../core/DropdownMenu';
import { RadioButtonGroup } from '../../core/RadioButtonGroup';

interface AdvancedOptionsSectionLocalProps {
    configData: SftpImportConfigurationWorkflow;
    onConfigChange: (data: Partial<SftpImportConfigurationWorkflow>) => void;
    readOnly?: boolean;
    collapsible?: boolean;
    defaultExpanded?: boolean;
}

interface AdvancedOptionsSectionState {
    showAdvanced: boolean;
}

type AdvancedOptionsSectionProps = AdvancedOptionsSectionLocalProps & ToxnotComponentProperties;

class AdvancedOptionsSectionComponent extends ToxnotComponent<AdvancedOptionsSectionProps, AdvancedOptionsSectionState> {
    constructor(props: AdvancedOptionsSectionProps) {
        super(props);
        this.state = {
            showAdvanced: props.defaultExpanded || !props.collapsible || false
        };
    }

    private toggleAdvanced = () => {
        if (this.props.collapsible) {
            this.setState(prevState => ({ showAdvanced: !prevState.showAdvanced }));
        }
    }

    render() {
        const { configData, onConfigChange, readOnly = false, collapsible = true, appServices } = this.props;
        const { showAdvanced } = this.state;
        
        // Get localized descriptions
        const calculationModeDescriptions = getCalculationModeTypeDescriptions(appServices);
        const inventoryMatchModeDescriptions = getInventoryIdentificationMatchModeDescriptions(appServices);
        const supplierMatchModeDescriptions = getSupplierIdentificationMatchModeDescriptions(appServices);

        const content = (
            <div className="start-subcontent-content-container big-margin-bottom">
                <div className="form-group">
                    <label>{this.localizeString("Tags")}</label>
                    <TagsInput
                        value={configData.tags || []}
                        onChange={(tags) => !readOnly && onConfigChange({ tags })}
                        placeholder={"Add a tag and press Enter"}
                        disabled={readOnly}
                    />
                </div>
                <div className="form-group">
                    <label>{this.localizeString("Auto-calculation Mode")}</label>
                    {!readOnly && (
                        <a href="http://help.exchange.3eco.com/getting-started/weight-auto-calculations" target="_blank" style={{ marginLeft: '10px', fontSize: '12px' }}>
                            {this.localizeString("What's this?")}
                        </a>
                    )}
                    <DropdownMenu
                        options={Object.entries(CalculationModeType).map(([key, value]) => ({
                            key: value,
                            name: calculationModeDescriptions[value] || value
                        }))}
                        value={{ key: configData.calculationMode, name: calculationModeDescriptions[configData.calculationMode] || "" }}
                        onOptionSelected={option => !readOnly && onConfigChange({ calculationMode: option.key })}
                        getFriendlyNameForOptions={option => option.name}
                        getUniqueValueForOption={option => option.key}
                        isDisabled={readOnly}
                    />
                </div>
                <div className="form-group">
                    <label>{this.localizeString("Extract and Link Materials Options")}</label>
                    <CheckBox
                        checked={configData.shouldExtractAndLink}
                        onValueChange={checked => !readOnly && onConfigChange({ shouldExtractAndLink: checked, shouldMatchMaterials: !checked })}
                        checkboxLabelText={this.localizeString("Extract and link materials")}
                        isDisabled={readOnly}
                    />
                </div>
                {configData.shouldExtractAndLink && (
                    <div style={{ paddingLeft: '35px' }}>
                        <CheckBox
                            checked={configData.extractAndLinkShouldOverwriteSubmaterials || false}
                            onValueChange={checked => !readOnly && onConfigChange({ extractAndLinkShouldOverwriteSubmaterials: checked })}
                            isDisabled={!configData.shouldExtractAndLink || readOnly}
                            checkboxLabelText={this.localizeString("Overwrite existing sub-materials content instead of just linking to existing sub-materials")}
                        />
                        <CheckBox
                            checked={configData.shouldExtractLeafiestMaterials || false}
                            onValueChange={checked => !readOnly && onConfigChange({ shouldExtractLeafiestMaterials: checked })}
                            isDisabled={!configData.shouldExtractAndLink || readOnly}
                            checkboxLabelText={this.localizeString("Extract materials from lowest-level rows")}
                        />
                    </div>
                )}

                {!configData.shouldExtractAndLink && (
                    <CheckBox
                        checked={!configData.shouldMatchMaterials}
                        onValueChange={checked => !readOnly && onConfigChange({ shouldMatchMaterials: !checked })}
                        checkboxLabelText={this.localizeString("Don't automatically link materials in products with existing materials")}
                        isDisabled={readOnly}
                    />
                )}
                <div className="form-group">
                    <label>{this.localizeString("How should 3E Exchange match your import to existing Products/Materials?")}</label>
                    <RadioButtonGroup<InventoryIdentificationMatchMode>
                        options={[
                            InventoryIdentificationMatchMode.Default,
                            InventoryIdentificationMatchMode.Strict,
                            InventoryIdentificationMatchMode.OnlyName,
                            InventoryIdentificationMatchMode.IgnoreSupplier,
                            InventoryIdentificationMatchMode.IgnoreDescription
                        ]}
                        value={configData.inventoryIdentificationMatchMode}
                        onValueChange={(value) => !readOnly && onConfigChange({ inventoryIdentificationMatchMode: value })}
                        getFriendlyNameForOptions={(option) => inventoryMatchModeDescriptions[option]}
                        name="inventory-match-mode"
                        isDisabled={readOnly}
                    />
                </div>
                <div className="form-group">
                    <label>{this.localizeString("How should 3E Exchange match your import to existing Suppliers?")}</label>
                    <RadioButtonGroup<SupplierIdentificationMatchMode>
                        options={[
                            SupplierIdentificationMatchMode.Default,
                            SupplierIdentificationMatchMode.NameAndUserFacingSupplierId,
                            SupplierIdentificationMatchMode.UserFacingSupplierIdOnly,
                            SupplierIdentificationMatchMode.NameOnly
                        ]}
                        value={configData.supplierIdentificationMatchMode}
                        onValueChange={(value) => !readOnly && onConfigChange({ supplierIdentificationMatchMode: value })}
                        getFriendlyNameForOptions={(option) => supplierMatchModeDescriptions[option]}
                        name="supplier-match-mode"
                        isDisabled={readOnly}
                    />
                </div>
            </div>
        );

        if (!collapsible) {
            return (
                <div>
                    <div className="start-subcontent-heading">
                        {this.localizeString("Advanced Options")}
                    </div>
                    {content}
                </div>
            );
        }

        return (
            <div id="advanced-options-container" style={{ display: 'block' }}>
                <div 
                    id="hide-advanced-options-button" 
                    className="start-subcontent-heading" 
                    onClick={this.toggleAdvanced} 
                    style={{ cursor: 'pointer' }}
                >
                    {this.localizeString("Advanced Options")} 
                    <span className={`glyphicon ${showAdvanced ? 'glyphicon-chevron-up' : 'glyphicon-chevron-down'}`}></span>
                </div>
                {showAdvanced && content}
            </div>
        );
    }
}

export const AdvancedOptionsSection = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(AdvancedOptionsSectionComponent);
